import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
// import toast, { Toaster } from "react-hot-toast";
import { toast } from "sonner";

import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import "@livekit/components-styles";

import { useSocket } from "@/helpers/useSocket";

import JungleTableStage from "./JungleTableStage";
import Icon from "./ui/Icon";

import { joinCacheKey } from "./utils/identity";
import { useDeviceCheck } from "./hooks/useDeviceCheck";

import {
  leaveSessionTable,
  leaveSessionTableKeepalive,
  switchTable,
} from "@/Services/EventMeetingServices";

import "./MeetingRoom.css";
import "./ParticipantsPanel.css";

export default function MeetingRoom() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const sessionId = state?.sessionId ?? null;
  const eventId = state?.eventId ?? null;

  const [currentTableId, setCurrentTableId] = useState(state?.tableId ?? null);
  const [roomResponse, setRoomResponse] = useState(state?.response ?? null);

  const [currentTableName, setCurrentTableName] = useState(
    state?.tableName ?? "Meeting",
  );
  const [currentTableIcon, setCurrentTableIcon] = useState(
    state?.response?.data?.tableIcon || state?.tableIcon || null,
  );

  const authToken = useMemo(() => {
    return (
      state?.authToken ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      ""
    );
  }, [state?.authToken]);

  const { socket, on, joinSession } = useSocket(authToken);

  const rawServerUrl = roomResponse?.data?.livekitServerUrl || "";
  const token =
    roomResponse?.data?.tableToken || roomResponse?.data?.livekitToken || "";

  const permissionsFromApi = roomResponse?.data?.permissions || {};
  const permissions = {
    canPublishAudio: permissionsFromApi?.canPublishAudio ?? true,
    canPublishVideo: permissionsFromApi?.canPublishVideo ?? true,
    canPublishScreen: permissionsFromApi?.canPublishScreen ?? true,
    canPublishData: permissionsFromApi?.canPublishData ?? true,
  };

  const initialMicEnabled = state?.initialMicEnabled ?? true;
  const initialCameraEnabled = state?.initialCameraEnabled ?? true;

  // ✅ device check hook (same behavior)
  const { deviceStatus, setDeviceStatus } = useDeviceCheck({
    initialMicEnabled,
    initialCameraEnabled,
    canPublishAudio: permissions.canPublishAudio,
    canPublishVideo: permissions.canPublishVideo,
  });

  const audio = deviceStatus.audioAllowed && permissions.canPublishAudio;
  const video = deviceStatus.videoAllowed && permissions.canPublishVideo;

  const [connect, setConnect] = useState(true);
  const [uiError, setUiError] = useState("");
  const [connected, setConnected] = useState(false);

  const [queueModal, setQueueModal] = useState(null);
  const [seatModal, setSeatModal] = useState(null);

  const pendingSwitchRef = useRef(false);

  const apiParticipants = roomResponse?.data?.participants ?? [];
  const apiUserMap = useMemo(() => {
    const entries = (Array.isArray(apiParticipants) ? apiParticipants : []).map(
      (p) => [p._id, p],
    );
    return Object.fromEntries(entries);
  }, [apiParticipants]);

  const serverUrl = useMemo(() => {
    if (!rawServerUrl) return "";
    const isHttps = window.location.protocol === "https:";
    let url = rawServerUrl;

    if (isHttps) {
      if (url.startsWith("ws://")) url = url.replace(/^ws:\/\//, "wss://");
      else if (url.startsWith("http://"))
        url = url.replace(/^http:\/\//, "https://");
    }
    return url;
  }, [rawServerUrl]);

  useEffect(() => {
    document.title = "Meeting Room | theglobalapp";
  }, []);

  useEffect(() => {
    if (!sessionId || !currentTableId || !rawServerUrl || !token) {
      setUiError(
        "Missing meeting details. Please go back and join the table again.",
      );
    } else {
      setUiError("");
    }
  }, [sessionId, currentTableId, rawServerUrl, token]);

  useEffect(() => {
    if (!sessionId) return;

    if (socket?.connected) joinSession(sessionId);

    const cleanup = on("connect", () => joinSession(sessionId));
    return cleanup;
  }, [sessionId, socket?.connected, joinSession, on]);

  useEffect(() => {
    if (!sessionId) return;

    const cleanup = on("table-seat-available", (payload) => {
      if (String(payload?.sessionId) !== String(sessionId)) return;
      if (payload?.requestedAction !== "switch") return;

      if (
        payload?.sourceTableId &&
        String(payload.sourceTableId) !== String(currentTableId)
      ) {
        return;
      }

      setQueueModal(null);
      setSeatModal(payload);
    });

    return cleanup;
  }, [on, sessionId, currentTableId]);

  useEffect(() => {
    if (!seatModal?.notificationExpiresAt) return;

    const expiresAt = new Date(seatModal.notificationExpiresAt).getTime();
    const ttlMs = expiresAt - Date.now();

    if (ttlMs <= 0) return;

    const t = setTimeout(() => setSeatModal(null), ttlMs);
    return () => clearTimeout(t);
  }, [seatModal?.notificationExpiresAt]);

  const wasConnectedRef = useRef(false);
  const didLeaveRef = useRef(false);
  const skipLeaveApiRef = useRef(false);
  const didNavigateRef = useRef(false);
  const connectionTimeoutRef = useRef(null);
  const isSwitchingRef = useRef(false);

  const leaveOnce = useCallback(async () => {
    if (didLeaveRef.current) return;
    if (!sessionId) return;

    // if we never connected, just clean cache
    if (!wasConnectedRef.current) {
      if (sessionId && currentTableId)
        sessionStorage.removeItem(joinCacheKey(sessionId, currentTableId));
      return;
    }

    didLeaveRef.current = true;

    try {
      await leaveSessionTable(sessionId);
    } catch (_) {
      // ignore
    } finally {
      if (sessionId && currentTableId)
        sessionStorage.removeItem(joinCacheKey(sessionId, currentTableId));
    }
  }, [sessionId, currentTableId]);

  const handleRequestTableChange = useCallback(
    async (newTable) => {
      const newTableId = newTable?._id || newTable?.id;
      if (!newTableId || !sessionId) return;
      if (String(newTableId) === String(currentTableId)) return;

      try {
        let apiResp;

        // Call switch API FIRST (do not disconnect)
        try {
          apiResp = await switchTable(sessionId, newTableId);
        } catch (err) {
          const maybeQueued = err?.response?.data;
          if (maybeQueued?.queued === true) {
            setQueueModal(maybeQueued?.data || maybeQueued);
            return; // stay in current table
          }
          throw err;
        }

        // queued case (sometimes comes as 200)
        if (apiResp?.queued === true || apiResp?.data?.queued === true) {
          setQueueModal(apiResp?.data || apiResp);
          return; // stay in current table
        }

        // success case
        const nextServerUrl = apiResp?.data?.livekitServerUrl;
        const nextToken =
          apiResp?.data?.tableToken || apiResp?.data?.livekitToken;

        if (!nextServerUrl || !nextToken) {
          throw new Error("Switch API did not return LiveKit credentials.");
        }

        pendingSwitchRef.current = true;
        isSwitchingRef.current = true;
        skipLeaveApiRef.current = true;

        if (currentTableId) {
          sessionStorage.removeItem(joinCacheKey(sessionId, currentTableId));
        }
        sessionStorage.setItem(
          joinCacheKey(sessionId, newTableId),
          JSON.stringify(apiResp),
        );

        setCurrentTableId(newTableId);
        setRoomResponse(apiResp);
        setCurrentTableName(newTable?.tableName || "Meeting");
        setCurrentTableIcon(
          newTable?.sponcer?.image || newTable?.tableIcon || null,
        );

        // LiveKitRoom remounts due to key={`${sessionId}:${currentTableId}`}
        setConnect(true);
      } catch (e) {
        toast.error(e?.message || "Failed to switch table");
      }
    },
    [sessionId, currentTableId],
  );

  // ✅ Ask confirmation on tab close/refresh, and only if user REALLY leaves -> call leave API
useEffect(() => {
  if (!sessionId || !currentTableId) return;

  // 1) Show browser confirmation dialog
  const onBeforeUnload = (e) => {
    // if we've already left, don't prompt
    if (didLeaveRef.current) return;

    e.preventDefault();
    e.returnValue = ""; // required for Chrome to show prompt
  };

  // 2) Runs only when the document is actually being unloaded (user clicked "Leave")
  const onPageHide = () => {
    if (didLeaveRef.current) return;
    didLeaveRef.current = true;

    // ✅ best-effort leave call during unload
    leaveSessionTableKeepalive(sessionId);

    // cleanup cache
    if (sessionId && currentTableId) {
      sessionStorage.removeItem(joinCacheKey(sessionId, currentTableId));
    }
  };

  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("pagehide", onPageHide);

  return () => {
    window.removeEventListener("beforeunload", onBeforeUnload);
    window.removeEventListener("pagehide", onPageHide);
  };
}, [sessionId, currentTableId]);

  useEffect(() => {
    if (!connect) return;

    connectionTimeoutRef.current = setTimeout(() => {
      if (!wasConnectedRef.current) {
        setUiError(
          "Connection timeout. The meeting server is unreachable. Please try again.",
        );
        setConnect(false);
      }
    }, 15000);

    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    };
  }, [connect]);

  const handleRoomConnected = useCallback(() => {
    wasConnectedRef.current = true;
    setConnected(true);

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (pendingSwitchRef.current) {
      pendingSwitchRef.current = false;
      skipLeaveApiRef.current = false;
      isSwitchingRef.current = false;
    }
  }, []);

  const handleEndCall = useCallback(async () => {
    skipLeaveApiRef.current = false;
    setConnect(false);

    await leaveOnce();

    if (!didNavigateRef.current) {
      didNavigateRef.current = true;
      if (eventId && sessionId) {
        navigate(`/lobby?event=${eventId}&session=${sessionId}`, {
          replace: true,
        });
      } else {
        navigate(-2);
      }
    }
  }, [leaveOnce, navigate, eventId, sessionId]);

  const handleRoomDisconnected = useCallback(async () => {
    setConnected(false);

    // switching causes a deliberate disconnect; don't leave or navigate
    if (isSwitchingRef.current) return;

    if (!skipLeaveApiRef.current) {
      await leaveOnce();
    }

    if (!didNavigateRef.current) {
      didNavigateRef.current = true;
      if (eventId && sessionId) {
        navigate(`/lobby?event=${eventId}&session=${sessionId}`, {
          replace: true,
        });
      } else {
        navigate(-2);
      }
    }
  }, [leaveOnce, navigate, eventId, sessionId]);

  const handleRoomError = useCallback((e) => {
    if (e?.message?.includes("Client initiated disconnect")) return;

    const isSSLError =
      e?.message?.includes("SSL") ||
      e?.message?.includes("certificate") ||
      e?.message?.includes("ERR_CERT") ||
      e?.message?.includes("secure connection") ||
      e?.message?.includes("Failed to fetch");

    if (isSSLError) {
      setUiError(
        "SSL/Certificate Error: Unable to establish a secure connection to the meeting server. Please contact support.",
      );
    } else {
      setUiError(e?.message || "LiveKit connection error");
    }
  }, []);

  if (deviceStatus.checking) {
    return (
      <div className="jt-error">
        <div className="jt-errorCard">
          <div className="jt-spinner" style={{ margin: "0 auto 20px" }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
          <h2>Preparing meeting...</h2>
          <p>Checking camera and microphone availability</p>
        </div>
      </div>
    );
  }

  if (uiError) {
    const isSSLError =
      uiError.includes("SSL") ||
      uiError.includes("certificate") ||
      uiError.includes("secure connection");

    return (
      <div className="jt-error">
        <div className="jt-errorCard">
          <h2>Unable to join meeting</h2>
          <p>{uiError}</p>

          {isSSLError && (
            <div className="jt-sslWarn">
              <strong>SSL Certificate Issue Detected</strong>
              <p>
                The meeting server has an invalid/missing SSL certificate. This
                is a server-side configuration issue.
              </p>
            </div>
          )}

          <button
            className="jt-primaryBtn"
            onClick={async () => {
              await leaveOnce();
              navigate(-1);
            }}
            type="button"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-room-container jt-container">
      {/* <Toaster position="top-right" /> */}

      {/* queued modal */}
      {queueModal && (
        <div
          className="jt-modalOverlay"
          onMouseDown={() => setQueueModal(null)}
        >
          <div className="jt-sheet" onMouseDown={(e) => e.stopPropagation()}>
            <div className="jt-sheetHead">
              <div className="jt-sheetTitle">
                {queueModal?.modal?.title || "Table is full"}
              </div>
              <button
                className="jt-sheetClose"
                onClick={() => setQueueModal(null)}
              >
                <Icon name="close" />
              </button>
            </div>
            <div className="jt-sheetBody">
              <p>
                {queueModal?.modal?.message ||
                  queueModal?.waitMessage ||
                  "This table is full. Please wait."}
              </p>
              {queueModal?.queuePosition != null && (
                <div style={{ marginTop: 8 }}>
                  Position: {queueModal.queuePosition}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* seat available modal */}
      {seatModal && (
        <div className="jt-modalOverlay" onMouseDown={() => setSeatModal(null)}>
          <div className="jt-sheet" onMouseDown={(e) => e.stopPropagation()}>
            <div className="jt-sheetHead">
              <div className="jt-sheetTitle">
                {seatModal?.modal?.title || "Seat available"}
              </div>
              <button
                className="jt-sheetClose"
                onClick={() => setSeatModal(null)}
              >
                <Icon name="close" />
              </button>
            </div>
            <div className="jt-sheetBody">
              <p>
                {seatModal?.message ||
                  `A seat is now available at ${seatModal?.tableName || "the table"
                  }.`}
              </p>

              <button
                className="jt-primaryBtn"
                type="button"
                onClick={() => {
                  handleRequestTableChange({
                    _id: seatModal.tableId,
                    tableName: seatModal.tableName,
                    tableIcon: null,
                    sponcer: null,
                  });
                  setSeatModal(null);
                }}
              >
                Switch now
              </button>
            </div>
          </div>
        </div>
      )}

      <LiveKitRoom
        key={`${sessionId}:${currentTableId}`}
        serverUrl={serverUrl}
        token={token}
        connect={connect}
        audio={audio}
        video={video}
        data-lk-theme="default"
        options={{ adaptiveStream: true, dynacast: true }}
        onConnected={handleRoomConnected}
        onDisconnected={handleRoomDisconnected}
        onError={handleRoomError}
        className="livekit-room-custom jt-livekit"
      >
        <RoomAudioRenderer />

        <JungleTableStage
          tableName={currentTableName}
          tableIcon={currentTableIcon}
          connected={connected}
          apiParticipants={apiParticipants}
          apiUserMap={apiUserMap}
          permissions={permissions}
          onLeave={handleEndCall}
          sessionId={sessionId}
          eventId={eventId}
          currentTableId={currentTableId}
          onRequestTableChange={handleRequestTableChange}
          authToken={authToken}
          deviceStatus={deviceStatus}
          setDeviceStatus={setDeviceStatus}
        />
      </LiveKitRoom>
    </div>
  );
}